import {
  Component,
  Output,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  ViewChild,
} from "@angular/core";
import { NG_VALUE_ACCESSOR } from "@angular/forms";
import { AssetService } from "../../providers/asset";
import { UiService } from "../../providers/ui";

declare var CKEDITOR: any;

@Component({
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => HtmlEditor),
      multi: true,
    },
  ],
  selector: "html-editor",
  templateUrl: "html-editor.html",
})
export class HtmlEditor {
  @Output() contentChange: EventEmitter<string>;
  @Output() onChangeEvent: EventEmitter<string>;
  @Output() onReadyEvent: EventEmitter<string>;
  @Output() onFocusEvent: EventEmitter<string>;
  @Output() onBlurEvent: EventEmitter<string>;
  @Output() onFileUploadEvent: EventEmitter<string>;

  @ViewChild("editor") editorElement: ElementRef;

  _config: any;
  _content: string;
  _editor: any;

  constructor(public assetService: AssetService, public uiService: UiService) {
    this.contentChange = new EventEmitter<string>();
    this.onChangeEvent = new EventEmitter<string>();
    this.onReadyEvent = new EventEmitter<string>();
    this.onFocusEvent = new EventEmitter<string>();
    this.onBlurEvent = new EventEmitter<string>();
    this.onFileUploadEvent = new EventEmitter<string>();
  }

  // Lifecycle hooks
  ngAfterViewInit() {
    this.init();
  }

  ngAfterViewChecked() {
    this.init();
  }

  ngOnDestroy() {
    if (!this._editor) {
      return null;
    }

    setTimeout(() => {
      this._editor.removeAllListeners();
      CKEDITOR.instances[this._editor.name].destroy();
      this._editor.destroy();
      this._editor = null;
    });
  }

  // Event handlers
  onReady(event: any) {
    this.onReadyEvent.emit(event);
  }

  onFocus(event: any) {
    this.onFocusEvent.emit(event);
  }

  onBlur(event: any) {
    this.onBlurEvent.emit(event);
  }

  onChange(event: any) {
    this.content = event.editor.getData();

    this.onChangeEvent.emit(this.content);
  }

  onFileUpload(event: any) {
    this.onFileUploadEvent.emit(event);
  }

  // Editor helpers
  init() {
    this.loadScripts(this.initEditor.bind(this));
  }

  initEditor() {
    if (!this.isEditorLoaded) {
      return null;
    }
    if (!this.documentContains(this.editorElement.nativeElement)) {
      return null;
    }
    if (this._editor) {
      return null;
    }

    // Init plugins
    CKEDITOR.plugins.addExternal(
      "youtube",
      "/assets/js/ckeditor/plugins/youtube/",
      "plugin.js",
    );

    // Init editor
    this._editor = CKEDITOR.replace(
      this.editorElement.nativeElement,
      this.config,
    );
    this._editor.setData(this.content);

    // Bind events
    this._editor.on("ready", this.onReady.bind(this));
    this._editor.on("focus", this.onFocus.bind(this));
    this._editor.on("blur", this.onBlur.bind(this));
    this._editor.on("change", this.onChange.bind(this));
    this._editor.on("fileUpload", this.onFileUpload.bind(this));

    // Customise UI
    CKEDITOR.on(
      "dialogDefinition",
      (events) => {
        // Add Firebase image upload button
        this.addFirebaseImageUploadButton.bind(this);
      }
    );
  }

  addFirebaseImageUploadButton(event) {
    let dialogName = event.data.name;
    let dialogDefinition = event.data.definition;
    let dialog = dialogDefinition.dialog;

    if ("image" === dialogName) {
      let infoTab = dialogDefinition.getContents("info");
      if (!infoTab || infoTab.get("firebaseImageUploadFile")) {
        return null;
      }

      dialogDefinition.getContents("info").add({
        id: "firebaseImageUploadFile",
        type: "file",
        label: "Your file",
        validate: CKEDITOR.dialog.validate.notEmpty(
          "Please select an image to upload.",
        ),
        onChange: data => {
          let input = data.sender.getInputElement().$;
          if (!input.files || !input.files.length) {
            return null;
          }

          let loader = this.uiService.createLoader();

          this.assetService
            .loadFileFromObject(input.files[0])
            .then(image => {
              return this.assetService.uploadArticleImage(image);
            })
            .then(uploadedImage => {
              let urlInput = dialog.getContentElement("info", "txtUrl");
              if (!urlInput) {
                return null;
              }

              urlInput.setValue(uploadedImage.downloadURL);

              return loader.dismiss();
            })
            .catch(error => {
              return loader.dismiss().then(() => {
                return this.uiService.showAlert(
                  "Whoops!",
                  "Could upload selected image, please try again.",
                );
              });
            });
        },
      });
    }
  }

  loadScripts(callback: any) {
    if (this._editor || this.isEditorLoaded) {
      callback.apply(this);

      return null;
    }

    const script = document.createElement("script");
    script.src = this.editorScriptUrl;
    script.type = "text/javascript";
    script.onload = () => {
      callback.apply(this);
    };

    document.body.appendChild(script);
  }

  private documentContains(node: Node) {
    return document.contains
      ? document.contains(node)
      : document.body.contains(node);
  }

  get editorScriptUrl() {
    return "https://cdn.ckeditor.com/4.9.2/standard-all/ckeditor.js";
  }

  get isEditorLoaded() {
    return window.hasOwnProperty("CKEDITOR");
  }

  // Getters and setters
  @Input()
  get config(): any {
    if (!this._config) {
      this._config = this.defaultConfig;
    }

    return this._config;
  }

  set config(config: any) {
    this._config = config || {};
  }

  @Input()
  get content(): string {
    if (!this._content) {
      this._content = "";
    }

    return this._content;
  }

  set content(content: string) {
    this._content = content;
    this.contentChange.emit(this._content);
  }

  get debounce() {
    return this.getConfigOption("debounce", 200);
  }

  set debounce(debounce: boolean) {
    this.setConfigOption("debounce", debounce);
  }

  get readOnly() {
    return this.getConfigOption("readOnly", false);
  }

  set readOnly(readonly: boolean) {
    this.setConfigOption("readOnly", readonly);
  }

  getConfigOption(key, defaultValue = null) {
    if (!this._config) {
      this._config = this.config || {};
    }
    if (!this.config.hasOwnProperty(key)) {
      return defaultValue;
    }
    if (this.config[key] !== null) {
      return defaultValue;
    }

    return this.config[key];
  }

  setConfigOption(key, value) {
    this._config[key] = value;

    return this;
  }

  get defaultConfig() {
    return {
      allowedContent: true,
      customConfig: "",
      extraPlugins: "uploadimage,youtube",
      youtube_related: false,
      youtube_responsive: true,
      youtube_width: "290",
      youtube_height: "160",
      height: 600,
      toolbarCanCollapse: true,
      toolbar: this.defaultToolbarConfig,
    };
  }

  get defaultToolbarConfig() {
    return [
      {
        name: "clipboard",
        items: [
          "Cut",
          "Copy",
          "Paste",
          "PasteText",
          "PasteFromWord",
          "-",
          "Undo",
          "Redo",
        ],
      },
      {
        name: "basicstyles",
        items: [
          "Bold",
          "Italic",
          "Underline",
          "Strike",
          "Subscript",
          "Superscript",
          "-",
          "CopyFormatting",
          "RemoveFormat",
        ],
      },
      {
        name: "paragraph",
        items: [
          "NumberedList",
          "BulletedList",
          "-",
          "Outdent",
          "Indent",
          "-",
          "Blockquote",
          "CreateDiv",
          "-",
          "JustifyLeft",
          "JustifyCenter",
          "JustifyRight",
          "JustifyBlock",
          "-",
          "BidiLtr",
          "BidiRtl",
          "Language",
        ],
      },
      "/",
      {
        name: "styles",
        items: ["Styles", "Format", "Font", "FontSize"],
      },
      {
        name: "links",
        items: ["Link", "Unlink", "Anchor"],
      },
      {
        name: "insert",
        items: ["Image", "Youtube", "Table", "HorizontalRule", "-", "Source"],
      },
    ];
  }
}



// WEBPACK FOOTER //
// ./src/components/html-editor/html-editor.ts